import { SHStack, SHTypography } from "@components/design-systems";
import { AnalysisNameSkeleton } from "@pages/platform-analysis-v2/_id/components/skeletons/analysis-name";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { useEffect, useMemo, useState } from "react";
import { useUserPermissions } from "@hooks/userUserPermission";
import { usePractice } from "@hooks/usePractice";
import { isEmpty } from "lodash";
import { useSupplier } from "@hooks/useSupplier";

export const AnalysisName = () => {
  const { user, isSupplierGroup, isAdviserGroup, isSupplierBdmSale } =
    useUserPermissions();
  const {
    bannerName,
    ui: { isLoadingBanner },
  } = useSelector((state: RootState) => state.platformAnalysis);

  const { loadPractice } = usePractice();
  const { loadSupplier, refreshSupplier } = useSupplier();

  // States
  const [adviserFirmName, setAdviserFirmName] = useState<string | null>(null);

  useEffect(() => {
    const supplierId = user?.userMetadata?.supplier_id;
    !!supplierId && loadSupplier(supplierId);
    return () => {
      refreshSupplier();
    };
    // eslint-disable-next-line
  }, [user?.userMetadata?.supplier_id]);

  // Load adviser firm name
  useEffect(() => {
    if (!isAdviserGroup) return;
    const adviserFirmId = user?.userMetadata?.adviser_firm_id || "";
    loadPractice(adviserFirmId).then((practice) => {
      setAdviserFirmName(practice?.name || null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userMetadata?.adviser_firm_id]);

  const ownerName = useMemo(() => {
    const originalBannerOwnerName = bannerName?.ownerName;
    if (isSupplierGroup) return originalBannerOwnerName;
    return originalBannerOwnerName || "Adviser Name";
  }, [bannerName?.ownerName, isSupplierGroup]);

  const firmName = useMemo(() => {
    if (isSupplierGroup) return bannerName?.supplierName;
    return adviserFirmName;
  }, [adviserFirmName, bannerName?.supplierName, isSupplierGroup]);

  const analysisAdviserName = useMemo(() => {
    return bannerName?.adviserName;
  }, [bannerName?.adviserName]);

  const analysisPracticeName = useMemo(() => {
    return bannerName?.practiceName;
  }, [bannerName?.practiceName]);

  const showAdviserNameForSupplier = useMemo(() => {
    if (!isSupplierBdmSale) return false;
    const isEmptyAdviser = isEmpty((analysisAdviserName || "").trim());
    const isEmptyPractice = isEmpty((analysisPracticeName || "").trim());
    return !isEmptyAdviser && !isEmptyPractice;
  }, [analysisAdviserName, analysisPracticeName, isSupplierBdmSale]);

  const adviserFamilyGroup = useMemo(() => {
    if (isSupplierGroup) return "";
    return bannerName?.familyGroupName || "Family Group";
  }, [bannerName?.familyGroupName, isSupplierGroup]);

  if (isLoadingBanner) return <AnalysisNameSkeleton />;

  return (
    <SHStack
      sx={{
        flexGrow: 1,
        width: "1px",
      }}
    >
      <SHStack
        direction="row"
        justifyContent="flex-start"
        alignItems={"flex-end"}
        spacing={0.5}
      >
        {ownerName && (
          <>
            <SHTypography
              variant="h2"
              fontSize={"18px"}
              lineHeight={1}
              truncate
              sx={{
                maxWidth: "60%",
              }}
            >
              {ownerName}
            </SHTypography>
          </>
        )}
        {firmName && (
          <SHTypography
            variant="h2"
            lineHeight={1}
            fontSize={"18px"}
            fontWeight={400}
            truncate
            sx={{
              maxWidth: "40%",
            }}
          >
            {`of ${firmName}`}
          </SHTypography>
        )}
      </SHStack>

      {isAdviserGroup && (
        <SHStack
          direction="row"
          justifyContent="flex-start"
          alignItems={"flex-end"}
          spacing={0.5}
        >
          <>
            <SHTypography
              variant="h2"
              lineHeight={1}
              fontSize={"18px"}
              fontWeight={400}
              truncate
              sx={{
                maxWidth: "40%",
              }}
            >
              {`for`}
            </SHTypography>
            <SHTypography
              variant="h2"
              fontSize={"18px"}
              lineHeight={1}
              truncate
              sx={{
                maxWidth: "60%",
              }}
            >
              {adviserFamilyGroup}
            </SHTypography>
          </>
        </SHStack>
      )}

      {isSupplierGroup && (
        <>
          {showAdviserNameForSupplier ? (
            <SHStack
              direction="row"
              justifyContent="flex-start"
              alignItems={"flex-end"}
              spacing={0.5}
            >
              <>
                <SHTypography variant="body2">for</SHTypography>
                <SHTypography
                  variant="subtitle2"
                  truncate
                  sx={{
                    maxWidth: "calc(50% - 20px)",
                  }}
                >
                  {analysisAdviserName}
                </SHTypography>
                <SHTypography
                  variant="body2"
                  truncate
                  sx={{
                    maxWidth: "50%",
                  }}
                >
                  {`from ${analysisPracticeName}`}
                </SHTypography>
              </>
            </SHStack>
          ) : (
            <SHTypography variant="body2">for internal use only</SHTypography>
          )}
        </>
      )}
    </SHStack>
  );
};
